import { useEffect, useMemo, useState } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PersonaSchema } from "@dawa/types";
import { z } from "zod";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/api";
import {
  Input,
  Button,
  Card,
  Select,
  useToast,
  EmptyState,
  PageHeader,
  FormField,
  PageLayout,
  Toolbar,
  Pagination,
  Badge,
  KpiCard,
  Textarea,
} from "@dawa/ui";
import { Users, Plus, GraduationCap } from "lucide-react";
import { motion } from "framer-motion";

type PersonaLevel = z.infer<typeof PersonaSchema>["level"];
type PersonaRecord = z.infer<typeof PersonaSchema> & {
  id: string;
  created_at?: string;
};

const splitList = (value?: string) =>
  value
    ? value
        .split(/[\n,]+/)
        .map((x) => x.trim())
        .filter(Boolean)
    : [];

const normalizeList = (value: unknown) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item.trim() : String(item)))
      .filter(Boolean);
  }
  if (typeof value === "string") return splitList(value);
  return [];
};

const toInputValue = (values?: string[]) => (values?.length ? values.join("\n") : "");

const isPersonaLevel = (value: unknown): value is PersonaLevel =>
  value === "beginner" || value === "intermediate" || value === "expert";

const FormSchema = PersonaSchema.extend({
  pains: z.string().optional().transform(splitList),
  goals: z.string().optional().transform(splitList),
});
type FormValues = z.input<typeof FormSchema>;

const colors = [
  "bg-danger/10 text-danger",
  "bg-success/10 text-success",
  "bg-accent/10 text-accent",
  "bg-ring/10 text-ring",
];
const colorFor = (text: string) => {
  let sum = 0;
  for (const c of text) sum += c.charCodeAt(0);
  return colors[sum % colors.length];
};

const levelLabels: Record<PersonaLevel, string> = {
  beginner: "Débutant",
  intermediate: "Intermédiaire",
  expert: "Expert",
};

const levelBadgeVariant: Record<PersonaLevel, "neutral" | "info" | "success"> = {
  beginner: "neutral",
  intermediate: "info",
  expert: "success",
};

export default function Personas() {
  const [list, setList] = useState<PersonaRecord[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { addToast, Toasts } = useToast();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [levelFilter, setLevelFilter] = useState<"" | PersonaLevel>("");
  const pageSize = 9;

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { level: "intermediate" },
  });
  const {
    register: editRegister,
    handleSubmit: handleEditSubmit,
    reset: editReset,
    setFocus: setEditFocus,
    formState: { errors: editErrors, isSubmitting: isEditSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(FormSchema) });

  const handleInvalid = (formErrors: FieldErrors<FormValues>) => {
    const firstError = Object.keys(formErrors)[0] as keyof FormValues | undefined;
    if (firstError) setFocus(firstError);
    const message = "Veuillez corriger les champs du formulaire.";
    setFormError(message);
    addToast(message, "error");
  };

  const handleEditInvalid = (formErrors: FieldErrors<FormValues>) => {
    const firstError = Object.keys(formErrors)[0] as keyof FormValues | undefined;
    if (firstError) setEditFocus(firstError);
    const message = "Veuillez corriger les champs du formulaire.";
    setEditError(message);
    addToast(message, "error");
  };

  async function load() {
    const s = await supabase.auth.getSession();
    const token = s.data.session?.access_token;
    if (!token) return;
    const res = await apiFetch("/v1/personas", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) addToast("Échec du chargement des personas", "error");
    const data = await res.json().catch(() => []);
    const list = Array.isArray(data) ? data : [];
    if (!Array.isArray(data)) addToast("Format inattendu pour la liste des personas", "error");
    const normalized = list
      .map((p: any): PersonaRecord | null => {
        if (!p || typeof p.id !== "string" || typeof p.name !== "string") return null;
        return {
          id: p.id,
          name: p.name,
          role: typeof p.role === "string" && p.role.trim() ? p.role.trim() : undefined,
          level: isPersonaLevel(p.level) ? p.level : "intermediate",
          pains: normalizeList(p.pains),
          goals: normalizeList(p.goals),
          created_at: typeof p.created_at === "string" ? p.created_at : undefined,
        };
      })
      .filter((p): p is PersonaRecord => Boolean(p));
    setList(normalized);
    setPage(1);
  }

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const search = q.trim().toLowerCase();
    return list.filter((p) => {
      const matchesLevel = !levelFilter || p.level === levelFilter;
      if (!matchesLevel) return false;
      if (!search) return true;
      const haystack = [p.name, p.role, ...p.pains, ...p.goals]
        .filter(Boolean)
        .map((value) => value.toLowerCase());
      return haystack.some((value) => value.includes(search));
    });
  }, [list, q, levelFilter]);

  const total = filtered.length;
  const maxPage = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > maxPage) setPage(maxPage);
  }, [page, maxPage]);

  const slice = filtered.slice((page - 1) * pageSize, page * pageSize);

  const stats = useMemo(() => {
    const counts: Record<PersonaLevel, number> = {
      beginner: 0,
      intermediate: 0,
      expert: 0,
    };
    const roles = new Set<string>();
    filtered.forEach((p) => {
      counts[p.level] += 1;
      if (p.role) roles.add(p.role);
    });
    return { counts, uniqueRoles: roles.size };
  }, [filtered]);

  const isFiltered = filtered.length !== list.length;

  return (
    <PageLayout maxWidth="full" className="space-y-6">
      <PageHeader
        title="Personas"
        breadcrumbs={[{ label: "Dashboard", href: "/dashboard" }, { label: "Personas" }]}
        actions={
          <Button className="flex items-center gap-2" onClick={() => setShowForm((s) => !s)}>
            <Plus className="h-4 w-4" /> Ajouter
          </Button>
        }
      />
      <Toolbar
        searchValue={q}
        onSearchChange={(v) => {
          setQ(v);
          setPage(1);
        }}
        placeholder="Rechercher un persona"
        actions={
          <Select
            className="w-full sm:w-48"
            value={levelFilter}
            onChange={(event) => {
              setLevelFilter(event.target.value as PersonaLevel | "");
              setPage(1);
            }}
          >
            <option value="">Tous les niveaux</option>
            <option value="beginner">Débutant</option>
            <option value="intermediate">Intermédiaire</option>
            <option value="expert">Expert</option>
          </Select>
        }
      />

      {list.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          <KpiCard
            label="Personas correspondants"
            value={total}
            helper={
              isFiltered
                ? `${stats.uniqueRoles} rôles distincts · sur ${list.length} personas au total`
                : `${stats.uniqueRoles} rôles distincts`
            }
            icon={<Users className="h-8 w-8" />}
          />
          <KpiCard
            label="Experts disponibles"
            value={stats.counts.expert}
            helper={`Intermédiaires : ${stats.counts.intermediate} · Débutants : ${stats.counts.beginner}`}
            icon={<GraduationCap className="h-8 w-8" />}
          />
        </div>
      )}

      {showForm && (
        <form
          className="grid gap-2 sm:grid-cols-2"
          onSubmit={handleSubmit(async (payload) => {
            setFormError(null);
            const s = await supabase.auth.getSession();
            const token = s.data.session?.access_token;
            if (!token) {
              const message = "Session expirée. Merci de vous reconnecter.";
              setFormError(message);
              addToast(message, "error");
              return;
            }
            try {
              const res = await apiFetch("/v1/personas", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
              });
              if (res.ok) {
                addToast("Persona créé", "success");
                reset({ level: "intermediate" });
                setShowForm(false);
                await load();
              } else {
                const body = await res.json().catch(() => ({}));
                const message = body?.error || `Erreur ${res.status}`;
                setFormError(message);
                addToast(message, "error");
              }
            } catch (err) {
              const message = err instanceof Error ? err.message : "Erreur réseau";
              setFormError(message);
              addToast(message, "error");
            }
          }, handleInvalid)}
        >
          <FormField label="Nom" htmlFor="persona-name" required error={errors.name?.message}>
            <Input id="persona-name" placeholder="Nom" {...register("name")} />
          </FormField>
          <FormField label="Rôle" htmlFor="persona-role">
            <Input id="persona-role" placeholder="Rôle" {...register("role")} />
          </FormField>
          <FormField label="Niveau" htmlFor="persona-level">
            <Select id="persona-level" {...register("level")}>
              <option value="beginner">Débutant</option>
              <option value="intermediate">Intermédiaire</option>
              <option value="expert">Expert</option>
            </Select>
          </FormField>
          <FormField
            label="Douleurs"
            htmlFor="persona-pains"
            help="Une entrée par ligne ou séparées par des virgules"
            className="sm:col-span-2"
          >
            <Textarea
              id="persona-pains"
              placeholder="Douleurs (une par ligne)"
              {...register("pains")}
            />
          </FormField>
          <FormField
            label="Objectifs"
            htmlFor="persona-goals"
            help="Une entrée par ligne ou séparées par des virgules"
            className="sm:col-span-2"
          >
            <Textarea
              id="persona-goals"
              placeholder="Objectifs (un par ligne)"
              {...register("goals")}
            />
          </FormField>
          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" loading={isSubmitting}>
              Sauvegarder le persona
            </Button>
          </div>
          {formError && <p className="text-sm text-danger sm:col-span-2">{formError}</p>}
        </form>
      )}

      {filtered.length === 0 ? (
        list.length > 0 ? (
          <Card className="p-6 text-center text-sm text-muted">
            Aucun persona ne correspond aux filtres sélectionnés.
          </Card>
        ) : (
          <EmptyState
            title="Aucun persona"
            description="Créez votre premier persona pour mieux guider la génération de contenus."
            action={<Button onClick={() => setShowForm(true)}>Ajouter un persona</Button>}
          />
        )
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {slice.map((p) => (
            <motion.div key={p.id} whileHover={{ scale: 1.02 }}>
              <Card className="flex h-full flex-col justify-between p-4">
                {editing === p.id ? (
                  <form
                    className="space-y-2"
                    onSubmit={handleEditSubmit(async (payload) => {
                      setEditError(null);
                      const s = await supabase.auth.getSession();
                      const token = s.data.session?.access_token;
                      if (!token) {
                        const message = "Session expirée. Merci de vous reconnecter.";
                        setEditError(message);
                        addToast(message, "error");
                        return;
                      }
                      setEditError(null);
                      try {
                        const res = await apiFetch(`/v1/personas/${p.id}`, {
                          method: "PATCH",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify(payload),
                        });
                        if (res.ok) {
                          setEditing(null);
                          await load();
                          addToast("Persona mis à jour", "success");
                        } else {
                          const body = await res.json().catch(() => ({}));
                          const message = body?.error || `Erreur ${res.status}`;
                          setEditError(message);
                          addToast(message, "error");
                        }
                      } catch (err) {
                        const message = err instanceof Error ? err.message : "Erreur réseau";
                        setEditError(message);
                        addToast(message, "error");
                      }
                    }, handleEditInvalid)}
                  >
                    <FormField
                      label="Nom"
                      htmlFor={`persona-${p.id}-name`}
                      required
                      error={editErrors.name?.message}
                    >
                      <Input
                        id={`persona-${p.id}-name`}
                        placeholder="Nom"
                        {...editRegister("name")}
                      />
                    </FormField>
                    <FormField label="Rôle" htmlFor={`persona-${p.id}-role`}>
                      <Input
                        id={`persona-${p.id}-role`}
                        placeholder="Rôle"
                        {...editRegister("role")}
                      />
                    </FormField>
                    <FormField label="Niveau" htmlFor={`persona-${p.id}-level`}>
                      <Select id={`persona-${p.id}-level`} {...editRegister("level")}>
                        <option value="beginner">Débutant</option>
                        <option value="intermediate">Intermédiaire</option>
                        <option value="expert">Expert</option>
                      </Select>
                    </FormField>
                    <FormField label="Douleurs" htmlFor={`persona-${p.id}-pains`}>
                      <Textarea
                        id={`persona-${p.id}-pains`}
                        placeholder="Douleurs"
                        {...editRegister("pains")}
                      />
                    </FormField>
                    <FormField label="Objectifs" htmlFor={`persona-${p.id}-goals`}>
                      <Textarea
                        id={`persona-${p.id}-goals`}
                        placeholder="Objectifs"
                        {...editRegister("goals")}
                      />
                    </FormField>
                    {editError && <p className="text-sm text-danger">{editError}</p>}
                    <div className="flex justify-end gap-2">
                      <Button type="submit" loading={isEditSubmitting}>
                        Sauvegarder
                      </Button>
                      <Button type="button" variant="secondary" onClick={() => setEditing(null)}>
                        Annuler
                      </Button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-accent">
                        <Users className="h-6 w-6" />
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="space-y-2">
                          <h3 className="font-semibold">{p.name}</h3>
                          <div className="flex flex-wrap gap-2">
                            {p.role && (
                              <Badge
                                variant="neutral"
                                className={`text-xs uppercase tracking-wide ${colorFor(p.role)}`}
                              >
                                {p.role}
                              </Badge>
                            )}
                            {p.level && (
                              <Badge
                                variant={levelBadgeVariant[p.level]}
                                className="text-xs uppercase tracking-wide"
                              >
                                {levelLabels[p.level]}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {p.pains?.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs font-medium uppercase text-muted">Douleurs</p>
                            <div className="flex flex-wrap gap-2">
                              {p.pains.map((pain, idx) => (
                                <Badge key={`${p.id}-pain-${idx}`} variant="secondary">
                                  {pain}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {p.goals?.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs font-medium uppercase text-muted">Objectifs</p>
                            <div className="flex flex-wrap gap-2">
                              {p.goals.map((goal, idx) => (
                                <Badge key={`${p.id}-goal-${idx}`} variant="info">
                                  {goal}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          setEditing(p.id);
                          editReset({
                            name: p.name,
                            role: p.role || "",
                            level: p.level || "intermediate",
                            pains: toInputValue(p.pains),
                            goals: toInputValue(p.goals),
                          });
                          setEditError(null);
                        }}
                      >
                        Éditer
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        loading={actionLoading === p.id}
                        onClick={async () => {
                          const s = await supabase.auth.getSession();
                          const token = s.data.session?.access_token;
                          if (!token) return;
                          if (!window.confirm("Supprimer ce persona ?")) return;
                          setActionLoading(p.id);
                          try {
                            const res = await apiFetch(`/v1/personas/${p.id}`, {
                              method: "DELETE",
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (res.ok) {
                              addToast("Persona supprimé", "info");
                              await load();
                            } else {
                              const body = await res.json().catch(() => ({}));
                              addToast(body?.error || "Échec de la suppression", "error");
                            }
                          } catch (err) {
                            addToast(err instanceof Error ? err.message : "Erreur réseau", "error");
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        Supprimer
                      </Button>
                    </div>
                  </>
                )}
              </Card>
            </motion.div>
          ))}
        </div>
      )}
      {filtered.length > pageSize && (
        <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
      )}
      <Toasts />
    </PageLayout>
  );
}
